/**
 * Espelha o `proxifyCoverUrl` do shelf-import
 * (`functions/src/shelf-import/resolvers.ts`) — MANTENHA OS DOIS EM SINCRONIA
 * (travado por `tests/unit/proxifyCoverParity.test.ts`).
 *
 * É a forma canônica de uma capa GRAVADA em `user_books.coverUrl` (BUG-13):
 * URL crua (`http://books.google.com/...`) quebra a captura do Share Studio
 * (host sem CORS) e some no hosting https (mixed content). wsrv.nl serve com
 * CORS aberto, https e webp; `v` estável = CDN cacheia por livro.
 *
 * Existe no client por causa da auto-cura da estante (BUG-171): a capa que o
 * `book_meta` conhece pode estar CRUA lá (o catálogo prefere alta resolução
 * das fontes CORS-safe), e propagá-la crua pro `user_books` violaria o
 * contrato acima.
 */
export function proxifyCoverUrl(
  raw: string | null | undefined,
  cacheKey: string,
): string | null {
  if (!raw) return null;
  if (raw.startsWith("data:") || raw.includes("wsrv.nl")) return raw;
  let url = raw.replace(/^http:\/\//, "https://");
  if (/books\.google/.test(url)) {
    url = url.replace(/&edge=curl/g, "").replace(/&zoom=\d+/g, "") + "&zoom=3";
  }
  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&output=webp&v=${encodeURIComponent(cacheKey)}`;
}

/**
 * Proxifica uma URL de imagem externa via wsrv.nl.
 * Resolve CORS para html-to-image sem necessidade de fetch/base64.
 * Guard anti-double-proxy: URLs já com wsrv.nl são retornadas direto.
 *
 * @param slotId  Identificador único do slot (avatar, book0, bg…)
 * @param cacheKey Rotacionado por geração para evitar colisões de cache
 */
export function proxifyImageUrl(url: string, slotId: string, cacheKey = ""): string {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  const v = cacheKey ? `${slotId}_${cacheKey}` : slotId;

  // URL já proxificada: só ganha `v` se ainda não tiver um.
  //
  // ⚠️ **Antes isto SOBRESCREVIA o `v` existente, e era metade do "card sem
  // capa"** (BUG-99). As capas de `user_books` já nascem com `v=<bookId>` —
  // `proxifyBookCover`, em `googleBooksApi.ts` —, que é único por livro e
  // portanto já cumpre o que este parâmetro existe pra cumprir. Reescrevê-lo
  // trocava a URL por uma que **o navegador nunca tinha buscado**: a estante
  // acabara de exibir a capa com `v=<bookId>`, e o Studio pedia `v=cover0` e
  // depois `v=opt` (o `getOptimizedBase64` reproxifica). URL diferente = entrada
  // de cache diferente, no cache do browser e no CDN — ou seja, **toda
  // exportação ia à rede fria**, contra o teto de 2,5s da casca. Com a chave
  // preservada, o caminho comum vira acerto de cache.
  //
  // Pior no `getOptimizedBase64`, que reescrevia TODAS as capas para `v=opt`:
  // ali o parâmetro produzia exatamente a colisão que ele deveria evitar.
  //
  // A garantia antiga continua de pé onde ela importa: URL sem `v` (avatar do
  // Cloudinary, imagem avulsa) continua recebendo o do slot.
  if (url.includes("wsrv.nl")) {
    try {
      const u = new URL(url);
      if (!u.searchParams.get("v")) u.searchParams.set("v", v);
      return u.toString();
    } catch {
      return url;
    }
  }

  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&output=webp&v=${encodeURIComponent(v)}`;
}
